import React from 'react'
import TemplateInput from '@/components/ui/TemplateInput'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import type { WorkflowNodeData } from '@/types/workflow'

interface HttpRequestConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, infoBannerClass, checkboxInputClass } from './_kit'

const METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
]

const HttpRequestConfig: React.FC<HttpRequestConfigProps> = ({ data, updateField }) => {
  const config = data.http_request_config || { url: '', method: 'POST', headers: {}, body_template: '{}', timeout_seconds: 10, store_response: false }
  const updateConfig = (key: string, value: any) => {
    updateField('http_request_config', { ...config, [key]: value })
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="HTTP Request"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <Field label="Method">
        {(f) => (
          <Select
            {...f}
            value={config.method || 'POST'}
            onChange={(value) => updateConfig('method', value)}
            options={METHOD_OPTIONS}
            className="w-full"
          />
        )}
      </Field>

      <Field label="URL">
        {(f) => (
          <TemplateInput
            {...f}
            value={config.url || ''}
            onChange={(v) => updateConfig('url', v)}
            placeholder="https://api.example.com/webhook/{{contact.id}} (type {{ for variables)"
            multiline={false}
          />
        )}
      </Field>

      {config.method !== 'GET' && (
        <Field label="Body Template">
          {(f) => (
            <TemplateInput
              {...f}
              value={config.body_template || '{}'}
              onChange={(v) => updateConfig('body_template', v)}
              placeholder='{"contact": "{{contact.full_name}}"} (type {{ for variables)'
              rows={4}
            />
          )}
        </Field>
      )}

      <Field label="Timeout (Seconds)">
        {(f) => (
          <input
            {...f}
            type="number"
            value={config.timeout_seconds ?? 10}
            onChange={(e) => updateConfig('timeout_seconds', parseInt(e.target.value) || 1)}
            min={1}
            max={30}
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="store_response"
          checked={config.store_response ?? false}
          onChange={(e) => updateConfig('store_response', e.target.checked)}
          className={checkboxInputClass}
        />
        <label htmlFor="store_response" className="text-sm text-foreground">
          Store response
        </label>
      </div>

      <div className={infoBannerClass}>
        <p className="text-xs">
          Supports {'{{contact.full_name}}'}, {'{{contact.email}}'}, {'{{contact.company_name}}'} and other template variables.
        </p>
      </div>
    </div>
  )
}

export default HttpRequestConfig
